import React, { useContext, useEffect, useState } from "react";
import * as FP from "utils/fp-js";
import { useWarnings } from "hooks/useWarnings";
import "./Dashboard.css";
import { EndIcon } from "./Dashboard.style";
import { GlobalState } from "App";

export const NUMBER_OF_LEVELS = Object.keys(EndIcon).length;
export const REFERRALS_IN_LEVEL = 50;

export const STARTER_MAX_PROGRESS = 20;
export const BRONZE_MAX_PROGRESS = 40;
export const SILVER_MAX_PROGRESS = 60;
export const GOLD_MAX_PROGRESS = 80;

export const mobileModes = {
  Profile: "Profile",
  Awards: "Awards",
  Dashboard: "Dashboard",
};

export const getUserLevel = (progress) =>
  FP.match(true)
    .on(progress >= 100, "Daimond")
    .on(progress >= GOLD_MAX_PROGRESS, "Platinum")
    .on(progress >= SILVER_MAX_PROGRESS, "Gold")
    .on(progress >= BRONZE_MAX_PROGRESS, "Silver")
    .on(progress >= STARTER_MAX_PROGRESS, "Bronze")
    .otherwise("Starter");

export const getUserPosition = (progress) =>
  FP.match(true)
    .on(progress >= 100, 5)
    .on(progress >= GOLD_MAX_PROGRESS, 10)
    .on(progress >= SILVER_MAX_PROGRESS, 20)
    .on(progress >= BRONZE_MAX_PROGRESS, 30)
    .on(progress >= STARTER_MAX_PROGRESS, 35)
    .otherwise(100);

export const getUserProgress = (referralsCount) =>
  (referralsCount * 100) / (REFERRALS_IN_LEVEL * NUMBER_OF_LEVELS);

export const useDashboard = () => {
  const { setAuthBackUrl } = useContext(GlobalState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [notification, setNotification] = useState("");
  const [originator, setOriginator] = useState(null);
  const [buttonLocations, setButtonLocations] = useState([]);
  const [senders, setSenders] = useState([]);
  const [providers, setProviders] = useState([]);
  const [progress, setProgress] = React.useState(0);
  const [levelProgress, setLevelProgress] = React.useState(0);
  const [newLevel, setNewLevel] = useState("");
  const [newCountsForUpdate, setNewCountsForUpdate] = useState("");
  const [logos, setLogos] = useState([]);
  const [modes, setModes] = useState([]);
  const [isInstallNedeed, setIsInstallNedeed] = useState(true);
  const [isButtonAdderOpen, setIsButtonAdderOpen] = useState(false);
  const [isRecipientAdmin, setIsRecipientAdmin] = useState(false);
  const [currentButton, setCurrentButton] = useState(null);
  const [oneColMode, setOneColRowMode] = useState(true);
  const [isPasswordCreateVisible, setIsPasswordCreateVisible] = useState(false);

  const addNewProvider = async (newProvider) => {
    if (!newProvider) return;
    const newProviders = [...providers, newProvider].sort((a, b) => {
      const nameA = a?.name?.toUpperCase();
      const nameB = b?.name?.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
    setProviders(newProviders);
  };

  const addNewSender = async (newSender) => {
    if (!newSender) return;
    const newSenders = [...senders, newSender].sort((a, b) => {
      const nameA = a.firstName.toUpperCase();
      const nameB = b.firstName.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
    setSenders(newSenders);
  };

  const toggleColMode = (mode) => {
    window.localStorage.setItem(
      `${originator?.id}-defaultColMode`,
      mode ? "true" : "false"
    );
    setOneColRowMode(mode);
  };

  const { showWarning, hideWarning, warning } = useWarnings();

  const handleBookmarkInfoClose = () => {
    setNotification("");
  };

  const handleBookmarkInfoButtonClick = () => {
    window.localStorage.setItem(
      `${originator?.id}-bookmarkInfoIsConfirmed`,
      "true"
    );
    setNotification("");
  };

  const setNewProgress = (referralsCount) => {
    if (referralsCount > 0) {
      const currenProgress = Math.round(
        (referralsCount * 100) / (REFERRALS_IN_LEVEL * NUMBER_OF_LEVELS)
      );

      const newLevelProgress = Math.round(
        ((referralsCount % REFERRALS_IN_LEVEL) / REFERRALS_IN_LEVEL) * 100
      );

      setLevelProgress(currenProgress >= 100 ? 100 : newLevelProgress);
      setProgress(currenProgress <= 100 ? currenProgress : 100);
    }
  };

  const checkToken = async () => {
    const accessToken = window.localStorage.getItem(`accessToken`);

    if (!accessToken) {
      setLoading(false);
      setIsInstallNedeed(true);
      return;
    }

    try {
      const buttonTokens = JSON.parse(
        window.localStorage.getItem(`buttonTokens`)
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check_access_token`,
        {
          method: "POST",
          body: JSON.stringify({ accessToken, buttonTokens }),
        }
      );
      const result = await response.json();

      if (result?.error || result?.message) {
        setError(result?.error || result?.message);
        return;
      }

      if (!result?.isTokenValid) {
        throw new Error();
      }

      setIsRecipientAdmin(result?.isRecipientAdmin ?? false);
      setOriginator(result?.originator ?? null);
      const sortedLocations = (result?.buttonLocations ?? []).sort((a, b) => {
        const aKey = Object.keys(a)[0];
        const bKey = Object.keys(b)[0];
        const nameA = a[aKey].recipient.name.toUpperCase();
        const nameB = b[bKey].recipient.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      setSenders(result?.senders ?? []);
      setProviders(result?.providers ?? []);
      setButtonLocations(sortedLocations ?? []);
      setNewProgress(result?.referralsCount ?? 0);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
    }
  };

  const hanldeLogout = () => {
    window.localStorage.removeItem(`accessToken`);
    setOriginator(null);
    setButtonLocations([]);
    setProgress(0);
    setLevelProgress(0);
    setNewLevel("");
    setNewCountsForUpdate("");
    setLogos([]);
    setModes([]);
    setIsInstallNedeed(true);
    setIsButtonAdderOpen(false);
    setIsRecipientAdmin(false);
  };

  useEffect(() => {
    if (originator?.id) {
      const isPasswordNotSet =
        window.localStorage.getItem(
          `${originator?.email}-is-password-not-set`
        ) === "true";
      setIsPasswordCreateVisible(isPasswordNotSet);

      const defaultColMode =
        window.localStorage.getItem(`${originator?.id}-defaultColMode`) !==
        "false";
      setOneColRowMode(defaultColMode);
    }
  }, [originator]);

  const loadLogo = async (id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/download_logo/${id}`,
        {
          method: "GET",
        }
      );
      const result = await response.json();

      if (!result?.error) {
        return result;
      }
      return undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  useEffect(() => {
    const loadAllLogo = async () => {
      if (buttonLocations?.length) {
        try {
          const newLogos = [];
          const newModes = [];
          for (const buttonLocation of buttonLocations) {
            const locationId = Object.keys(buttonLocation)[0];
            const { recipient, imageFromS3 } = buttonLocation[locationId];
            if (imageFromS3?.awsKey) {
              const imageUrl = imageFromS3?.awsKey
                ? `${process.env.REACT_APP_API_V2_URL}/organizations/skins/${recipient?.id}`
                : null;
              newLogos.push(imageUrl);
              newModes.push(imageFromS3?.mode);
            } else {
              const { image, mode } = await loadLogo(recipient?.id);
              newLogos.push(image);
              newModes.push(mode);
            }
          }

          setLogos(newLogos);
          setModes(newModes);
        } catch (error) {
          console.error(error);
        }
      }
    };

    loadAllLogo();
  }, [buttonLocations]);

  useEffect(() => {
    checkToken();
    setAuthBackUrl(`/`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userLevel = getUserLevel(progress);

  const updateSendersInButtonLocations = (locationId, senders) => {
    const newButtonLocations = buttonLocations.map((buttonLocation) => {
      const buttonLocationId = Object.keys(buttonLocation)[0];
      if (buttonLocationId === locationId) {
        return {
          [buttonLocationId]: {
            ...buttonLocation[buttonLocationId],
            senders,
          },
        };
      }
      return buttonLocation;
    });

    setButtonLocations(newButtonLocations);
  };

  return {
    loading,
    setLoading,
    error,
    setError,
    setProgress,
    notification,
    setNotification,
    originator,
    buttonLocations,
    progress,
    levelProgress,
    newLevel,
    newCountsForUpdate,
    logos,
    modes,
    oneColMode,
    toggleColMode,
    showWarning,
    hideWarning,
    warning,
    handleBookmarkInfoClose,
    handleBookmarkInfoButtonClick,
    setNewProgress,
    userLevel,
    setNewLevel,
    setNewCountsForUpdate,
    setLevelProgress,
    isInstallNedeed,
    isButtonAdderOpen,
    setIsButtonAdderOpen,
    reloadButtons: () => checkToken(),
    isRecipientAdmin,
    hanldeLogout,
    senders,
    currentButton,
    setCurrentButton,
    updateSendersInButtonLocations,
    addNewSender,
    providers,
    addNewProvider,
    isPasswordCreateVisible,
    setIsPasswordCreateVisible,
  };
};
